import React, { useRef, useState } from 'react';
import ResultRow, { type SyntaxNode } from './ResultRow.js';
import LoadingSkeleton from './LoadingSkeleton.js';

// CRITICAL colSpan pattern — never hardcode colSpan={4}
const TABLE_COLUMNS = [
  { key: 'symbol',     label: 'Symbol',      scope: 'col' as const },
  { key: 'type',       label: 'Type',        scope: 'col' as const },
  { key: 'file-path',  label: 'File Path',   scope: 'col' as const },
  { key: 'repository', label: 'Repository',  scope: 'col' as const },
] as const;

export const flatTableColCount = TABLE_COLUMNS.length; // = 4

interface ResultsTableProps {
  results: SyntaxNode[];
  query: string;
  currentPage: number;
  pageSize: number;
  loading?: boolean;
}

// Nested grouping structure: repo → file → class → method → tokens
interface MethodGroup {
  methodName: string;
  tokens: SyntaxNode[];
}

interface ClassGroup {
  className: string;
  methods: Map<string, MethodGroup>;
}

interface FileGroup {
  filePath: string;
  classes: Map<string, ClassGroup>;
}

interface RepoGroup {
  repoId: string;
  files: Map<string, FileGroup>;
}

function groupResults(nodes: SyntaxNode[]): Map<string, RepoGroup> {
  const repos = new Map<string, RepoGroup>();

  for (const node of nodes) {
    const repoKey = node.repositoryId;
    if (!repos.has(repoKey)) {
      repos.set(repoKey, { repoId: repoKey, files: new Map() });
    }
    const repo = repos.get(repoKey)!;

    const fileKey = node.filePath;
    if (!repo.files.has(fileKey)) {
      repo.files.set(fileKey, { filePath: fileKey, classes: new Map() });
    }
    const file = repo.files.get(fileKey)!;

    // Null class/method: use empty string so grouping is consistent
    const classKey = node.containingClassName ?? '';
    if (!file.classes.has(classKey)) {
      file.classes.set(classKey, { className: classKey, methods: new Map() });
    }
    const cls = file.classes.get(classKey)!;

    const methodKey = node.containingMethodName ?? '';
    if (!cls.methods.has(methodKey)) {
      cls.methods.set(methodKey, { methodName: methodKey, tokens: [] });
    }
    cls.methods.get(methodKey)!.tokens.push(node);
  }

  return repos;
}

export default function ResultsTable({
  results,
  query,
  currentPage,
  pageSize,
  loading = false,
}: ResultsTableProps): React.ReactElement {
  const tableRef = useRef<HTMLTableElement>(null);

  const startIdx = (currentPage - 1) * pageSize;
  const pageResults = results.slice(startIdx, startIdx + pageSize);

  const [collapsedRepos, setCollapsedRepos] = useState<Set<string>>(() => new Set());
  const [collapsedFiles, setCollapsedFiles] = useState<Set<string>>(() => new Set());
  const [collapsedClasses, setCollapsedClasses] = useState<Set<string>>(() => new Set());
  const [collapsedMethods, setCollapsedMethods] = useState<Set<string>>(() => new Set());

  function toggle(key: string, setter: React.Dispatch<React.SetStateAction<Set<string>>>): void {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  function handleTableKeyDown(e: React.KeyboardEvent<HTMLTableElement>): void {
    const rows = Array.from(tableRef.current?.querySelectorAll('tr[tabindex="0"]') ?? []);
    const idx = rows.indexOf(document.activeElement as HTMLTableRowElement);
    if (e.key === 'ArrowDown' && idx >= 0 && idx < rows.length - 1) {
      e.preventDefault();
      (rows[idx + 1] as HTMLElement).focus();
    } else if (e.key === 'ArrowUp' && idx > 0) {
      e.preventDefault();
      (rows[idx - 1] as HTMLElement).focus();
    }
  }

  const grouped = groupResults(pageResults);

  function renderGroupedRows(): React.ReactNode[] {
    const rows: React.ReactNode[] = [];

    for (const repo of grouped.values()) {
      const repoCollapsed = collapsedRepos.has(repo.repoId);

      rows.push(
        <tr
          key={`repo-${repo.repoId}`}
          className="group-header group-header--repo"
          data-testid={`repo-group-header-${repo.repoId}`}
          onClick={() => toggle(repo.repoId, setCollapsedRepos)}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggle(repo.repoId, setCollapsedRepos);
            }
          }}
          aria-expanded={!repoCollapsed}
        >
          <td colSpan={flatTableColCount} className="group-header__cell group-header__cell--repo">
            <span className={`group-toggle ${repoCollapsed ? 'group-toggle--collapsed' : 'group-toggle--expanded'}`} aria-hidden="true" />
            {repo.repoId}
          </td>
        </tr>,
      );

      if (repoCollapsed) continue;

      for (const file of repo.files.values()) {
        const fileKey = `${repo.repoId}|${file.filePath}`;
        const fileCollapsed = collapsedFiles.has(fileKey);

        rows.push(
          <tr
            key={`file-${fileKey}`}
            className="group-header group-header--file"
            data-testid={`file-group-header-${fileKey}`}
            onClick={() => toggle(fileKey, setCollapsedFiles)}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggle(fileKey, setCollapsedFiles);
              }
            }}
            aria-expanded={!fileCollapsed}
          >
            <td colSpan={flatTableColCount} className="group-header__cell group-header__cell--file">
              <span className={`group-toggle ${fileCollapsed ? 'group-toggle--collapsed' : 'group-toggle--expanded'}`} aria-hidden="true" />
              {file.filePath || <span className="file-path-empty" aria-label="file path unknown">—</span>}
            </td>
          </tr>,
        );

        if (fileCollapsed) continue;

        for (const cls of file.classes.values()) {
          const classKey = `${fileKey}|${cls.className}`;
          const classCollapsed = collapsedClasses.has(classKey);

          // Omit the class header row when className is empty (null in original data)
          if (cls.className !== '') {
            rows.push(
              <tr
                key={`class-${classKey}`}
                className="group-header group-header--class"
                data-testid={`class-group-header-${classKey}`}
                onClick={() => toggle(classKey, setCollapsedClasses)}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggle(classKey, setCollapsedClasses);
                  }
                }}
                aria-expanded={!classCollapsed}
              >
                <td colSpan={flatTableColCount} className="group-header__cell group-header__cell--class">
                  <span className={`group-toggle ${classCollapsed ? 'group-toggle--collapsed' : 'group-toggle--expanded'}`} aria-hidden="true" />
                  {cls.className}
                </td>
              </tr>,
            );

            if (classCollapsed) continue;
          }

          for (const method of cls.methods.values()) {
            const methodKey = `${classKey}|${method.methodName}`;
            const methodCollapsed = collapsedMethods.has(methodKey);

            // Omit the method header row when methodName is empty (null in original data)
            if (method.methodName !== '') {
              rows.push(
                <tr
                  key={`method-${methodKey}`}
                  className="group-header group-header--method"
                  data-testid={`method-group-header-${methodKey}`}
                  onClick={() => toggle(methodKey, setCollapsedMethods)}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggle(methodKey, setCollapsedMethods);
                    }
                  }}
                  aria-expanded={!methodCollapsed}
                >
                  <td colSpan={flatTableColCount} className="group-header__cell group-header__cell--method">
                    <span className={`group-toggle ${methodCollapsed ? 'group-toggle--collapsed' : 'group-toggle--expanded'}`} aria-hidden="true" />
                    {method.methodName}
                  </td>
                </tr>,
              );

              if (methodCollapsed) continue;
            }

            for (const token of method.tokens) {
              rows.push(
                <ResultRow key={token.nodeId} result={token} query={query} />,
              );
            }
          }
        }
      }
    }

    return rows;
  }

  return (
    <div className="results-table-wrap">
      <table
        ref={tableRef}
        role="grid"
        data-testid="results-table"
        className="results-table"
        onKeyDown={handleTableKeyDown}
      >
        <thead>
          <tr>
            {TABLE_COLUMNS.map((col) => (
              <th key={col.key} scope={col.scope} aria-sort="none">{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <LoadingSkeleton colSpan={flatTableColCount} rows={8} />
          ) : (
            renderGroupedRows()
          )}
        </tbody>
      </table>
    </div>
  );
}
